import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Lock, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/re`;
const when = (s) => new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Full blocking screen: the PACT Life Pass stays locked until every unread staff memo is confirmed. Staff viewing never see it. */
export default function MemoGate({ token }) {
  const [memos, setMemos] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/portal/memos/unread`, { params: { token } }).then((r) => setMemos(r.data.memos)).catch(() => setMemos([]));
  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.body.style.overflow = memos?.length ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [memos]);
  if (!memos?.length) return null;
  const m = memos[0];
  const staffReplies = (m.replies || []).filter((x) => x.by_kind === "staff");
  const confirm = () => {
    setBusy(true);
    axios.post(`${API}/portal/memos/${m.id}/confirm`, null, { params: { token } }).then(() => setMemos((x) => x.slice(1))).catch(load).finally(() => setBusy(false));
  };
  return createPortal(
    <div data-testid="memo-gate" aria-modal="true" aria-label="Unread memos" className="fixed inset-0 z-[9000] bg-[#0b0814] text-white overflow-y-auto" style={{ width: "100vw", height: "100dvh" }}>
      <div className="max-w-xl mx-auto px-5 py-10 space-y-5">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#d4a017]"><Lock className="h-3.5 w-3.5" /> Read before you continue</div>
        <h1 className="text-3xl font-bold leading-tight">You have {memos.length} unread memo{memos.length === 1 ? "" : "s"}</h1>
        <p className="text-sm text-white/60">Your PACT Life Pass opens again once you've read and confirmed each one.</p>
        <article data-testid={`memo-gate-memo-${m.id}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 space-y-3">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-[#d4a017] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <h2 data-testid="memo-gate-subject" className="text-lg font-semibold break-words">{m.subject}</h2>
              <p className="text-xs text-white/50">From {m.author?.name || "your staff"} · {when(m.created_at)}</p>
            </div>
          </div>
          <p data-testid="memo-gate-body" className="text-[15px] leading-relaxed whitespace-pre-wrap text-white/85">{m.body}</p>
          {staffReplies.map((x) => (
            <div key={x.id} className="border-l-2 border-[#d4a017]/60 pl-3">
              <p className="text-xs text-white/50">{x.author?.name} · {when(x.at)}</p>
              <p className="text-sm whitespace-pre-wrap text-white/85">{x.body}</p>
            </div>
          ))}
        </article>
        <button type="button" onClick={confirm} disabled={busy} data-testid="memo-gate-confirm"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d4a017] text-black font-semibold py-4 text-base transition-transform active:scale-[0.98] disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} I've read this{memos.length > 1 ? ` · ${memos.length - 1} more` : ""}
        </button>
      </div>
    </div>,
    document.body,
  );
}
